import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Button } from "@/components/ui/PageHeader";
import { getInsights, type AxisComposition } from "@/api/composer";

/**
 * Your post's receipt — who showed up, as composition.
 *
 * Deliberately not a reach counter. "Seen by 412 people" trains someone to
 * write for volume; "answered by Gujarati speakers in Edison and Jersey City"
 * tells them something true about who they reached and nothing about how to
 * game it. That is the whole design, and it is why there is no impressions
 * number anywhere on this page.
 *
 * Small groups are floored server-side before anything is sent, so a value can
 * never be traced back to one identifiable person. An empty axis is the normal
 * outcome for a young post, not a failure — the page says so instead of
 * rendering a hopeful skeleton.
 *
 * OP-only, enforced in the service. A 403 here is the gate working.
 */
export default function ComposerInsightsLab() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const postType = type === "question" ? "question" : "voice";

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["composer-insights", postType, id],
    queryFn: () => getInsights(postType, id!),
    enabled: Boolean(id),
    retry: false,
  });

  const insights = data?.data;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Who responded"
        subtitle="Composition, not a reach count — on purpose."
        breadcrumbs={[
          { label: "Lab", href: "/" },
          { label: "Composer", href: "/lab/composer" },
          { label: "Insights" },
        ]}
        actions={
          <Link to={`/lab/${postType === "voice" ? "voices" : "questions"}/${id}`}>
            <Button variant="secondary" size="sm">
              Open the post
            </Button>
          </Link>
        }
      />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {isError && (
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {/* The most likely reason by far, so name it rather than showing a
              bare error and letting someone chase a bug that isn't one. */}
          <p className="font-medium">Not yours to read</p>
          <p className="mt-1">
            These are visible only to whoever wrote the post. If you expected
            access, you are signed in as someone else.
          </p>
          <p className="mt-2 text-xs text-amber-800">
            {error instanceof Error ? error.message : "Request failed"}
          </p>
        </div>
      )}

      {insights && (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            {insights.headline ? (
              <p className="font-display text-xl text-gray-900">
                {insights.headline}
              </p>
            ) : (
              <p className="text-gray-600">
                Not enough responses yet to describe who they are.
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              {insights.responses.total}{" "}
              {insights.responses.total === 1 ? "person has" : "people have"}{" "}
              {postType === "question" ? "answered" : "joined in"}.
            </p>
          </section>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Axis label="Where they're from" axis={insights.composition.originStates} />
            <Axis label="Languages" axis={insights.composition.languages} />
            <Axis label="Cities" axis={insights.composition.cities} />
          </div>

          <p className="mt-5 rounded-xl bg-gray-50 px-4 py-3 text-xs leading-relaxed text-gray-500">
            Groups smaller than three are removed before this leaves the server,
            so nothing here can be narrowed down to one person. An empty column
            means the responses were too few or too mixed to describe — not that
            nobody came.
          </p>
        </>
      )}
    </div>
  );
}

function Axis({ label, axis }: { label: string; axis: AxisComposition }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>
      {axis.values.length === 0 ? (
        <p className="mt-2 text-sm text-gray-400">Too few to say</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {axis.values.map((v) => (
            <li key={v} className="text-sm text-gray-900">
              {v}
              {axis.mostly === v && (
                <span className="ml-1.5 text-xs text-gray-500">mostly</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
